import {io, Socket} from 'socket.io-client'
import {ref} from 'vue'
import {resolveApiPath} from '@/services/infrastructure/ServerBasePathResolver'
import {authStateInitialized, getAccessToken} from '@/services/infrastructure/auth.service'
import emitter from '@/services/infrastructure/emitter'
import {z} from 'zod'
import {selectedOrg} from '@/tenants/org.service'
import {session} from '@/services/infrastructure/session.service'
import {useInterval} from '@vueuse/core'
import {getFeatureValue} from '@/feature-toggles/feature-toggler.service'

const socket = ref<Socket>()

const PracsysMessageSchema = z.object({
    uuid: z.uuid(),
    sourceRef: z.string().nullish(),
    sentBy: z.string().nullish(),
    sentAt: z.iso.datetime(),
    tenantId: z.string().nullish()
})

type PracsysMessage = z.infer<typeof PracsysMessageSchema>

const buffer = ref(new Map<string, PracsysMessage>())
const bufferFlushInitialized = ref(false)


export const useSockets = async (depth: number = 0) => {
    if (depth > 5) return
    if (!getFeatureValue('socketsEnabled', false)) {
        console.debug('Sockets are disabled via feature toggle')
        return
    }
    const path = resolveApiPath()
    await authStateInitialized
    const token = await getAccessToken()

    socket.value = io(path + '/pracsys', {
        auth: {
            token: token
        },
        path: '/socket',
    })

    socket.value.on('connect', () => {
        console.debug('Socket connected')
    })

    // TODO fix when socket returns error code
    socket.value.on('connect_error', (error) => {
        console.warn('Socket connection error', error)
        if (error.message.includes('auth')) {
            console.debug('Reconnecting socket due to auth error')
            useSockets(depth + 1)
        }
    })

    if (!bufferFlushInitialized.value) {
        useInterval(1000, {
            callback: () => {
                if (buffer.value.size > 0) {
                    buffer.value.forEach((message, event) => {
                        if (message.tenantId === selectedOrg.value?.organizationId) {
                            emitter.emit(event, message)
                        }
                    })
                    buffer.value.clear()
                }
            }
        })
        bufferFlushInitialized.value = true
    }


    socket.value.onAny((eventName: string, ...args: any) => {
        try {
            const result = PracsysMessageSchema.safeParse(JSON.parse(args[0]))
            if (result.success) {
                handleMessage(eventName, result.data)
            } else {
                console.warn(`Failed to parse socket message for event ${eventName}: ${args}`)
            }
        } catch (e) {
            console.error(e)
        }

    })

    const handleMessage = (event: string, message: PracsysMessage) => {
        const sameTenant = !!message.tenantId && selectedOrg.value?.organizationId === message.tenantId
        const differentOrigin = session.value !== message.sourceRef
        console.debug(`Socket message received for event ${event}`, {message, sameTenant, differentOrigin})
        if (sameTenant && differentOrigin && !buffer.value.has(event)) {
            buffer.value.set(event, message)
        }
    }

}